/**
 * TimePickerComponent
 * Time input control with 12h/24h format support
 */

import React, { useCallback, useMemo, memo } from 'react';
import type { FormControlProps } from '../../types';

interface TimePickerComponentProps extends FormControlProps {
  format?: '12h' | '24h';
  step?: number;
  minTime?: string;
  maxTime?: string;
  defaultTime?: string;
  onChange?: (time: string) => void;
}

function formatTime(time: string, format: '12h' | '24h'): string {
  if (!time || format === '24h') return time;

  const [hours, minutes] = time.split(':');
  const h = parseInt(hours, 10);
  const meridiem = h >= 12 ? 'PM' : 'AM';
  const display12h = h % 12 || 12;

  return `${String(display12h).padStart(2, '0')}:${minutes} ${meridiem}`;
}

function parseTime(input: string, format: '12h' | '24h'): string {
  if (format === '24h') return input;

  const match = input.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!match) return '';

  let [, hours, minutes, meridiem] = match;
  let h = parseInt(hours, 10);

  if (meridiem.toUpperCase() === 'PM' && h !== 12) h += 12;
  if (meridiem.toUpperCase() === 'AM' && h === 12) h = 0;

  return `${String(h).padStart(2, '0')}:${minutes}`;
}

export const TimePickerComponent: React.FC<TimePickerComponentProps> = memo(({
  value = '',
  format = '24h',
  step = 15,
  minTime,
  maxTime,
  defaultTime = '',
  onChange,
  disabled = false,
  required = false,
  label,
  error,
  helpText,
}) => {
  const currentTime = (value as string) || defaultTime;

  const inputId = useMemo(
    () => `time-picker-${label?.replace(/\s+/g, '-').toLowerCase()}`,
    [label],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.currentTarget.value;
      const parsed = format === '12h' ? parseTime(val, '12h') : val;
      onChange?.(parsed);
    },
    [format, onChange],
  );

  const displayValue = formatTime(currentTime, format);

  return (
    <div className="af-form-field__group time-picker-group">
      {label && (
        <label htmlFor={inputId} className="af-form-field__label">
          {label}
          {required && <span className="af-form-field__required">*</span>}
        </label>
      )}

      <div className="time-picker__container">
        {format === '24h' ? (
          <input
            id={inputId}
            type="time"
            value={currentTime}
            onChange={handleChange}
            min={minTime}
            max={maxTime}
            step={step}
            disabled={disabled}
            className="time-picker__input"
            aria-label={label || 'Time picker'}
            aria-invalid={!!error}
            aria-describedby={error ? `${inputId}-error` : helpText ? `${inputId}-help` : undefined}
          />
        ) : (
          <div className="time-picker__12h-wrapper">
            <input
              id={inputId}
              type="text"
              value={displayValue}
              onChange={handleChange}
              placeholder="HH:MM AM/PM"
              disabled={disabled}
              className="time-picker__input time-picker__input--12h"
              aria-label={label || 'Time picker'}
              aria-invalid={!!error}
              aria-describedby={error ? `${inputId}-error` : helpText ? `${inputId}-help` : undefined}
            />
          </div>
        )}
      </div>

      {error && (
        <div id={`${inputId}-error`} className="af-form-field__error">
          {error}
        </div>
      )}

      {helpText && (
        <div id={`${inputId}-help`} className="af-form-field__help">
          {helpText}
        </div>
      )}
    </div>
  );
});
